import { useState } from 'react';
import { supabase } from '@/lib/supabase.ts';
import { Button, Input } from '@/components/ui.tsx';

/**
 * Why the sign-in screen is being shown, when it is not simply "you are not
 * signed in".
 *
 * A failed magic link redirects here with the reason in the URL fragment and
 * nothing else — no session, no toast, no clue. Landing on a bare form after
 * clicking a link that was supposed to sign you in reads as the app being
 * broken, which is the opposite of what happened: the link worked once and
 * cannot work twice. So read the fragment and say so.
 */
function linkFailure(): string | null {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  if (!params.get('error') && !params.get('error_code')) return null;

  // Clear it, or a reload keeps re-announcing a link somebody has moved on from.
  history.replaceState(null, '', window.location.pathname + window.location.search);

  const code = params.get('error_code');
  if (code === 'otp_expired') {
    return 'That sign-in link has already been used, or has expired. They work once — ask for a fresh one below.';
  }
  if (params.get('error') === 'access_denied') {
    return 'That sign-in link was refused. It may have been used already, or issued for a different address.';
  }
  return params.get('error_description')?.replace(/\+/g, ' ') ?? 'That sign-in link did not work.';
}

/**
 * Sign in.
 *
 * A link in your inbox and nothing else: no password to choose, forget, reuse
 * or leak. It also does the work of an invitation system — somebody invited by
 * address becomes a member the first time they click through, without anybody
 * having to create an account for them.
 */
export function SignIn() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  // Read once, on the first render, before anything can clear the fragment.
  const [linkError, setLinkError] = useState<string | null>(linkFailure);

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;

    setState('sending');
    setError(null);
    const { error: failure } = await supabase.auth.signInWithOtp({
      email: address,
      options: { emailRedirectTo: window.location.origin },
    });
    if (failure) {
      setError(failure.message);
      setState('idle');
      return;
    }
    setState('sent');
  };

  return (
    <div className="grid min-h-screen place-items-center bg-side px-6">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex items-center gap-2.5">
          <span className="size-6 rounded-md bg-accent" aria-hidden />
          <span className="text-[19px] font-bold tracking-[-0.01em] text-hi">Rigg</span>
        </div>

        {linkError ? (
          <div
            role="status"
            className="mb-5 rounded-xl border border-amberx/28 bg-amberx/8 px-3.5 py-3 text-[13px] leading-relaxed text-t3"
          >
            {linkError}
            <button
              onClick={() => setLinkError(null)}
              className="rg-focus mt-1.5 block cursor-pointer text-[12.5px] font-semibold text-mute hover:text-hi2"
            >
              Dismiss
            </button>
          </div>
        ) : null}

        {state === 'sent' ? (
          <>
            <h1 className="text-[19px] font-[650] tracking-[-0.01em] text-hi">Check your inbox</h1>
            <p className="mt-2 text-[13.5px] leading-relaxed text-body">
              A sign-in link is on its way to <strong className="text-t1">{email}</strong>. It is
              good for one use.
            </p>
            <Button className="mt-5" onClick={() => setState('idle')}>
              Use a different address
            </Button>
          </>
        ) : (
          <form onSubmit={send}>
            <h1 className="text-[19px] font-[650] tracking-[-0.01em] text-hi">Sign in</h1>
            <p className="mt-2 mb-5 text-[13.5px] leading-relaxed text-body">
              We will email you a link. No password to remember.
            </p>
            <Input
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              aria-label="Email address"
            />
            {error ? <p className="mt-2 text-[12.5px] text-warnx">{error}</p> : null}
            <Button
              type="submit"
              tone="primary"
              className="mt-4 w-full"
              disabled={state === 'sending' || !email.trim()}
            >
              {state === 'sending' ? 'Sending…' : 'Email me a link'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
